import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ListOrdered } from 'lucide-react';
import type { ApiError } from '../../lib/api';
import {
  resolverCaptura,
  sesionApi,
  sesionKeys,
  type ActividadDetalle,
} from '../../lib/sesion';

/**
 * CONDUCCIÓN DE RONDA (ADR-0005 §2) — «una sección por ronda».
 *
 * Visible solo cuando el manifiesto declara `capturas[].secciones`, la
 * actividad está abierta/pausada y quien mira puede conducir. Fijar una
 * sección hace que el Bolsillo muestre SOLO ese paso (viaja por el WS de la
 * sala) y que cada aporte registre payload.seccion; «Ritmo libre» levanta la
 * ronda y devuelve el wizard completo al participante.
 */
export function RondaControl({
  actividad,
  puedeConducir,
}: {
  actividad: ActividadDetalle;
  puedeConducir: boolean;
}) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (seccion: string | null) => sesionApi.marcarRonda(actividad.id, seccion),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sesionKeys.actividad(actividad.id) });
    },
  });

  const enCurso = actividad.estado === 'abierta' || actividad.estado === 'pausada';
  const res = resolverCaptura(actividad.herramienta);
  const secciones = res.soportada ? res.captura.secciones : [];
  if (!secciones.length || !enCurso || !puedeConducir) return null;

  const activa = actividad.ronda ?? null;
  const error = (mut.error as ApiError | null)?.detail ?? null;

  return (
    <section className="rounded-md border border-border-strong bg-surface-raised px-4 py-3">
      <p className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-content">
        <ListOrdered className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        Ronda por secciones
      </p>
      <p className="mt-1 font-sans text-xs text-content-muted">
        Fijá la sección que la sala trabaja ahora; cada aporte queda registrado con
        su sección. «Ritmo libre» deja el recorrido completo en manos de cada quien.
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={mut.isPending}
          onClick={() => mut.mutate(null)}
          aria-pressed={activa === null}
          className={`min-h-touch rounded-md border px-3 font-sans text-sm font-medium transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50 ${
            activa === null
              ? 'border-primary bg-surface-sunken text-content'
              : 'border-border-strong bg-surface-raised text-content-muted hover:border-primary'
          }`}
        >
          Ritmo libre
        </button>
        {secciones.map((s, i) => (
          <button
            key={s.id}
            type="button"
            disabled={mut.isPending}
            onClick={() => mut.mutate(s.id)}
            aria-pressed={activa === s.id}
            className={`min-h-touch rounded-md border px-3 font-sans text-sm font-medium transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50 ${
              activa === s.id
                ? 'border-primary bg-surface-sunken text-content'
                : 'border-border-strong bg-surface-raised text-content-muted hover:border-primary'
            }`}
          >
            {i + 1}. {s.titulo}
          </button>
        ))}
      </div>

      {error && (
        <p className="mt-2 flex items-center gap-1.5 font-sans text-sm text-error">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </section>
  );
}
